/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionClearStoreCache';

const ActionClearStoreCacheManager: ActionManagerFn<'ActionClearStoreCache'> = (
  platform: Platform,
): ActionManager<'ActionClearStoreCache'> => {
  return {
    ...baseActionManager(platform, 'ActionClearStoreCache'),
    defaultValue,
    props: {
      ...baseActionProps,
      alias: {
        label: 'Alias',
        type: 'DataSourceAliasChooser',
        required: true,
        allowedTypes: [
          'CloudDataStore',
          'ReadOnlyDataStore',
          'DataStore',
          'LocalDataStore',
          'TransientDataStore',
        ],
      },
    },
    name: 'Clear Store Cache',
    getDisplayText: (item) => item.props.alias,
    isDataSourceAliasUsed: (item, alias) => item.props.alias === alias,
    icon: { iconName: 'eraser' },
    replaceableWith: ['ActionClear'],
    description: `**Overview**<br/>
The Clear Store Cache action is a data lifecycle component designed to completely purge the contents of a specific Data Store along with any associated cached results. It serves as a deeper reset mechanism than standard data clearance, ensuring that both the active state and any underlying cached data for the selected entity are removed.<br/><br/>
This component is built to interact with a wide variety of data store architectures, ranging from persistent cloud stores to temporary local or transient stores. It explicitly supports component replacement workflows, functioning as a specialized alternative to the standard "Clear" action.

**Key Features**<br/>
* **Comprehensive Cache Purge:** Explicitly defined to clear "the store and all it's cached results," ensuring no stale data remains in memory or storage layers.
* **Broad Store Compatibility:** Supports configuration with multiple store types, including CloudDataStore, ReadOnlyDataStore, DataStore, LocalDataStore, and TransientDataStore.
* **Component Interchangeability:** Configured to be replaceable with ActionClear, allowing developers to switch between a standard clear and a cache-clearing operation within the editor.
* **Dependency Tracking:** Implements isDataSourceAliasUsed to track the usage of the specific data source alias, ensuring the platform can identify dependencies on the targeted store.

**Properties**<br/>
* **Alias (Required):** A DataSourceAliasChooser property that selects the specific Data Store instance to be cleared and de-cached.

**Use Cases**<br/>
* **Data Freshness:** Forcing a complete re-fetch of data from the backend by removing local cache, ensuring the user sees the absolute latest state.
* **Memory Management:** Freeing up resources by removing cached data from Transient or Local stores that is no longer needed.`,
  };
};

export default ActionClearStoreCacheManager;
